import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Search, Filter, X, Check, MapPin, RefreshCw, Plus, Loader, Settings, AlertCircle } from 'lucide-react';
import './DeviceSelector.css';

interface Device {
  id: number;
  serial?: string; // Para cámaras
  serialDevice?: string;
  serial_device?: string;
  mac?: string;
  idBrand?: number;
  id_brand?: number;
  brandName?: string;
  idModel?: number;
  id_model?: number;
  modelName?: string;
  model_name?: string;
  firmware?: string;
  isActive?: boolean;
  is_active?: boolean;
  siteId?: number; // ID del sitio asignado
  site_id?: number;
  siteName?: string; // Nombre del sitio asignado
  site_name?: string;
  assignedToSite?: string; // Nombre del sitio que tiene este dispositivo (legacy)
  assignedToSiteId?: number; // ID del sitio (legacy)
  [key: string]: any;
}

interface DeviceSelectorProps {
  devices: Device[];
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  title: string;
  icon: React.ReactNode;
  emptyMessage?: string;
  maxSelection?: number;
  searchPlaceholder?: string;
  currentSiteId?: number; // Para permitir seleccionar dispositivos del sitio actual
  onCreateNew?: () => void; // Callback para crear nuevo dispositivo
  showCreateButton?: boolean; // Mostrar botón de crear
  onReassign?: (deviceId: number, fromSiteId: number, fromSiteName: string) => void; // Callback para reasignar
  onFinish?: () => void; // Callback para terminar selección
  onRefresh?: () => Promise<void> | void; // Callback para refrescar inventario
  onEditDevice?: (deviceId: number) => void; // Callback para editar/configurar dispositivo
}

const DeviceSelector: React.FC<DeviceSelectorProps> = ({
  devices,
  selectedIds,
  onSelectionChange,
  title,
  icon,
  emptyMessage = 'No hay dispositivos disponibles',
  maxSelection,
  searchPlaceholder = 'Buscar por serial, MAC, marca o modelo...',
  currentSiteId,
  onCreateNew,
  showCreateButton = false,
  onReassign,
  onFinish,
  onRefresh,
  onEditDevice
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterBrand, setFilterBrand] = useState('');
  const [filterModel, setFilterModel] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterAvailability, setFilterAvailability] = useState<'all' | 'available' | 'assigned'>('all');
  const [maxWarningVisible, setMaxWarningVisible] = useState(false);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showMaxWarning = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    setMaxWarningVisible(true);
    warningTimerRef.current = setTimeout(() => setMaxWarningVisible(false), 4000);
  }, []);

  // Obtener listas únicas de marcas y modelos
  const { brands, models } = useMemo(() => {
    const brandSet = new Set<string>();
    const modelSet = new Set<string>();
    
    devices.forEach(device => {
      const brand = device.brandName;
      const model = device.modelName || device.model_name;
      if (brand) brandSet.add(brand);
      if (model) modelSet.add(model);
    });
    
    return {
      brands: Array.from(brandSet).sort(),
      models: Array.from(modelSet).sort()
    };
  }, [devices]);

  // Filtrar dispositivos
  const filteredDevices = useMemo(() => {
    return devices.filter(device => {
      // Búsqueda de texto
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const serial = (device.serial || device.serialDevice || device.serial_device || '').toLowerCase();
        const mac = (device.mac || '').toLowerCase();
        const brand = (device.brandName || '').toLowerCase();
        const model = (device.modelName || device.model_name || '').toLowerCase();
        
        const matches = serial.includes(search) ||
                       mac.includes(search) ||
                       brand.includes(search) ||
                       model.includes(search);
        
        if (!matches) return false;
      }

      // Filtro de marca
      if (filterBrand && device.brandName !== filterBrand) {
        return false;
      }

      // Filtro de modelo
      if (filterModel) {
        const model = device.modelName || device.model_name;
        if (model !== filterModel) return false;
      }

      // Filtro de estado activo
      if (filterActive !== 'all') {
        const isActive = device.isActive ?? device.is_active ?? true;
        if (filterActive === 'active' && !isActive) return false;
        if (filterActive === 'inactive' && isActive) return false;
      }

      // Filtro de disponibilidad
      if (filterAvailability !== 'all') {
        const deviceSiteId = device.siteId || device.site_id || device.assignedToSiteId;
        const isAssigned = deviceSiteId !== undefined && 
                          deviceSiteId !== null &&
                          deviceSiteId !== currentSiteId;
        if (filterAvailability === 'available' && isAssigned) return false;
        if (filterAvailability === 'assigned' && !isAssigned) return false;
      }

      return true;
    });
  }, [devices, searchTerm, filterBrand, filterModel, filterActive, filterAvailability, currentSiteId]);

  const toggleSelection = (deviceId: number, isAssigned: boolean, device: Device) => {
    if (isAssigned) {
      // Si está asignado a otro sitio, no permitir seleccionar directamente
      // El usuario debe usar el botón de reasignar
      return;
    }

    if (selectedIds.includes(deviceId)) {
      onSelectionChange(selectedIds.filter(id => id !== deviceId));
    } else {
      if (maxSelection && selectedIds.length >= maxSelection) {
        if (maxSelection === 1) {
          onSelectionChange([deviceId]);
        } else {
          showMaxWarning();
        }
        return;
      }
      onSelectionChange([...selectedIds, deviceId]);
    }
  };

  const handleReassign = (device: Device) => {
    const assignedSiteId = device.siteId || device.site_id || device.assignedToSiteId;
    const assignedSiteName = device.siteName || device.site_name || device.assignedToSite || `Sitio #${assignedSiteId}`;
    
    if (onReassign && assignedSiteId) {
      onReassign(device.id, assignedSiteId, assignedSiteName);
    }
  };

  const clearSelection = () => {
    onSelectionChange([]);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterBrand('');
    setFilterModel('');
    setFilterActive('all');
    setFilterAvailability('all');
  };

  const hasActiveFilters = searchTerm || filterBrand || filterModel || filterActive !== 'all' || filterAvailability !== 'all';

  return (
    <div className="device-selector">
      <div className="device-selector-header">
        <div className="device-selector-title">
          {icon}
          <h4>{title}</h4>
          <span className="device-selector-count">
            {selectedIds.length} seleccionados
            {maxSelection && ` de ${maxSelection} máx.`}
          </span>
        </div>
        
        <div className="device-selector-actions">
          {selectedIds.length > 0 && onFinish && (
            <button
              type="button"
              className="device-selector-btn-finish"
              onClick={onFinish}
            >
              <Check size={16} />
              Terminar selección
            </button>
          )}
          {onRefresh && (
            <button
              type="button"
              className="device-selector-btn-secondary"
              onClick={async () => {
                setRefreshing(true);
                try {
                  await onRefresh();
                } finally {
                  setRefreshing(false);
                }
              }}
              disabled={refreshing}
              title="Refrescar inventario"
            >
              {refreshing ? <Loader size={16} className="spin-animation" /> : <RefreshCw size={16} />}
              {refreshing ? 'Cargando...' : 'Refrescar'}
            </button>
          )}
          {showCreateButton && onCreateNew && (
            <button
              type="button"
              className="device-selector-btn-create"
              onClick={onCreateNew}
            >
              <Plus size={16} />
              Agregar Inventario
            </button>
          )}
          <button
            type="button"
            className="device-selector-btn-secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filtros
            {hasActiveFilters && <span className="filter-indicator" />}
          </button>
        </div>
      </div>

      {/* Banner de máximo alcanzado */}
      {maxWarningVisible && maxSelection && (
        <div className="device-selector-max-warning">
          <div className="device-selector-max-warning-content">
            <AlertCircle size={16} />
            <span>
              {maxSelection === 1
                ? 'Solo puedes asignar 1 dispositivo de este tipo por sitio. Para cambiarlo, primero deselecciona el actual.'
                : `Máximo ${maxSelection} dispositivos permitidos. Para cambiar, primero deselecciona uno.`}
            </span>
          </div>
          <button
            type="button"
            className="device-selector-max-warning-close"
            onClick={() => setMaxWarningVisible(false)}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Búsqueda */}
      <div className="device-selector-search">
        <Search size={18} className="search-icon" />
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="device-selector-search-input"
        />
        {searchTerm && (
          <button
            type="button"
            className="search-clear"
            onClick={() => setSearchTerm('')}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Panel de filtros */}
      {showFilters && (
        <div className="device-selector-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>Marca</label>
              <select
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value)}
                className="filter-select"
              >
                <option value="">Todas</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Modelo</label>
              <select
                value={filterModel}
                onChange={(e) => setFilterModel(e.target.value)}
                className="filter-select"
              >
                <option value="">Todos</option>
                {models.map(model => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Estado</label>
              <select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value as any)}
                className="filter-select"
              >
                <option value="all">Todos</option>
                <option value="active">Activos</option>
                <option value="inactive">Inactivos</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Disponibilidad</label>
              <select
                value={filterAvailability}
                onChange={(e) => setFilterAvailability(e.target.value as any)}
                className="filter-select"
              >
                <option value="all">Todos</option>
                <option value="available">Disponibles</option>
                <option value="assigned">Asignados</option>
              </select>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                className="filter-clear-btn"
                onClick={clearFilters}
              >
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      )}

      {/* Información de resultados */}
      <div className="device-selector-info">
        <span className="results-count">
          {filteredDevices.length} dispositivo{filteredDevices.length !== 1 ? 's' : ''} encontrado{filteredDevices.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Lista de dispositivos */}
      <div className="device-selector-list">
        {filteredDevices.length === 0 ? (
          <div className="device-selector-empty">
            {searchTerm || hasActiveFilters ? (
              <>
                <p>No se encontraron dispositivos con los filtros aplicados</p>
                <button
                  type="button"
                  className="clear-filters-btn"
                  onClick={clearFilters}
                >
                  Limpiar filtros
                </button>
              </>
            ) : (
              <p>{emptyMessage}</p>
            )}
          </div>
        ) : (
          <div className="device-table-wrapper">
            <table className="device-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Serial</th>
                  <th>MAC</th>
                  <th>Marca</th>
                  <th>Modelo</th>
                  <th style={{ width: '100px' }}></th>
                  <th>Sitio</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.map(device => {
                  const isSelected = selectedIds.includes(device.id);
                  const assignedSiteId = device.siteId || device.site_id || device.assignedToSiteId;
                  const hasAssignment = assignedSiteId !== undefined && assignedSiteId !== null;
                  const isAssignedToOther = hasAssignment && assignedSiteId !== currentSiteId;
                  const isAssignedToCurrent = hasAssignment && currentSiteId !== undefined && assignedSiteId === currentSiteId;
                  const assignedSiteName = device.siteName || device.site_name || device.assignedToSite;
                  
                  const serial = device.serial || device.serialDevice || device.serial_device || 'Sin serial';
                  const mac = device.mac || '-';
                  const brand = device.brandName || '-';
                  const model = device.modelName || device.model_name || '-';

                  return (
                    <tr
                      key={device.id}
                      className={`device-row ${isSelected ? 'selected' : ''} ${isAssignedToOther ? 'assigned-to-other' : ''} ${isAssignedToCurrent ? 'assigned-to-current' : ''}`}
                      onClick={() => toggleSelection(device.id, isAssignedToOther, device)}
                      style={{ cursor: isAssignedToOther ? 'not-allowed' : 'pointer' }}
                    >
                      <td>
                        <div className={`device-checkbox ${isAssignedToOther ? 'disabled' : ''}`}>
                          {isSelected && !isAssignedToOther && <Check size={16} />}
                        </div>
                      </td>
                      <td className={`device-serial ${isAssignedToOther ? 'dimmed-text' : ''}`}>{serial}</td>
                      <td className={`device-mac ${isAssignedToOther ? 'dimmed-text' : ''}`}>{mac}</td>
                      <td className={isAssignedToOther ? 'dimmed-text' : ''}>{brand}</td>
                      <td className={isAssignedToOther ? 'dimmed-text' : ''}>{model}</td>
                      <td className="device-actions-cell">
                        {isSelected && onEditDevice && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditDevice(device.id);
                            }}
                            className="device-edit-btn"
                            title="Configurar dispositivo"
                          >
                            <Settings size={14} />
                          </button>
                        )}
                        {isAssignedToOther && onReassign && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReassign(device);
                            }}
                            className="device-reassign-btn"
                            title="Reasignar a este sitio"
                          >
                            <RefreshCw size={14} />
                            Reasignar
                          </button>
                        )}
                      </td>
                      <td>
                        {isAssignedToOther ? (
                          <span className="device-site-badge-assigned">
                            <MapPin size={12} />
                            {assignedSiteName || `Sitio #${assignedSiteId}`}
                          </span>
                        ) : isAssignedToCurrent ? (
                          <span className="device-site-badge-current">
                            <MapPin size={12} />
                            Este sitio
                          </span>
                        ) : (
                          <span className="device-available-badge">Disponible</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceSelector;
